<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { cn } from '@/lib/utils'
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { VisuallyHidden } from 'reka-ui'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notifications'
import AppSidebar from './AppSidebar.vue'
import AppTopbar from './AppTopbar.vue'
import CallbackAlertModal from '@/components/CallbackAlertModal.vue'
import HelpFab from '@/components/HelpFab.vue'
import Tour from '@/components/Tour.vue'
import { useTheme } from '@/composables/useTheme'
import { getTourByRoute } from '@/tours'
import { useRealTimeUpdates, type AbsenceCheckEvent } from '@/composables/useRealTimeUpdates'
import api from '@/api/axios'
import { sendHeartbeat } from '@/api/heartbeat'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useToast } from '@/composables/useToast'
import type { TourStep } from '@/types/tour'

const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const { initTheme } = useTheme()
const router = useRouter()
const { error: toastError } = useToast()

const isCollapsed = ref(true)
const isPinned = ref(false)
const isHovered = ref(false)
const isMobile = ref(false)
const isMobileMenuOpen = ref(false)
const callbackModalRef = ref<InstanceType<typeof CallbackAlertModal> | null>(null)
const tourRef = ref<InstanceType<typeof Tour> | null>(null)
const absenceDialogOpen = ref(false)
const absenceLoading = ref(false)
const absenceCheckFor = ref<number | null>(null)

const { connect, subscribeToChannel, unsubscribeFromChannel, onAbsenceCheck } = useRealTimeUpdates()

const route = useRoute()
const tourId = computed(() => {
  const name = route.name ? String(route.name) : 'unknown'
  return `tour-${name}`
})
const currentTour = computed(() => {
  const name = route.name ? String(route.name) : undefined
  return name ? getTourByRoute(name) : undefined
})
const tourSteps = computed<TourStep[]>(() => currentTour.value?.steps ?? [])

const currentYear = computed(() => new Date().getFullYear())

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
  if (!isMobile.value) isMobileMenuOpen.value = false
}

const toggleSidebar = () => {
  isPinned.value = !isPinned.value
  isCollapsed.value = !isPinned.value
  // Al fijar con pin, nos aseguramos de quitar el estado hover
  if (isPinned.value) isHovered.value = false
}

// El sidebar se muestra expandido visualmente si no está colapsado
// O si el usuario pasa el ratón por encima mientras está colapsado (hover expand)
const sidebarExpanded = computed(() => !isCollapsed.value || isHovered.value)

const onSidebarMouseEnter = () => {
  // Solo hover-expand si el usuario NO tiene pineado el sidebar
  if (isCollapsed.value) isHovered.value = true
}

const onSidebarMouseLeave = () => {
  isHovered.value = false
}

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const onNavigate = () => {
  if (isMobile.value) isMobileMenuOpen.value = false
}

const startCurrentTour = () => {
  if (tourRef.value) {
    tourRef.value.restart()
  }
}

const onAbsenceCheckEvent = (event: AbsenceCheckEvent) => {
  const oid = authStore.user?.id
  if (!oid || event.operator_id !== oid) return
  absenceCheckFor.value = event.operator_id
  absenceDialogOpen.value = true
}

const confirmStillWorking = async () => {
  if (absenceLoading.value) return
  absenceLoading.value = true
  try {
    await sendHeartbeat()
  } catch {
    // Even if the heartbeat fails, we close the dialog so the user is not blocked.
  } finally {
    absenceLoading.value = false
    absenceDialogOpen.value = false
    absenceCheckFor.value = null
  }
}

const markAbsent = async () => {
  if (absenceLoading.value) return
  absenceLoading.value = true
  try {
    await api.post('/api/v1/operators/me/status/break', null, {
      params: { reason: 'ausencia' },
    })
  } catch (err: any) {
    const msg = err?.response?.data?.detail || 'No se pudo marcar ausencia.'
    toastError(msg)
  } finally {
    absenceLoading.value = false
    absenceDialogOpen.value = false
    absenceCheckFor.value = null
  }
}

onMounted(() => {
  initTheme()
  checkMobile()
  window.addEventListener('resize', checkMobile)
  window.addEventListener('tour:start-current', startCurrentTour)
  authStore.initAuth()

  // Subscribe to operator channel to receive absence checks anywhere in app.
  const oid = authStore.user?.id
  if (oid) {
    connect()
    subscribeToChannel(`operator:${oid}`)
    onAbsenceCheck(onAbsenceCheckEvent)
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
  window.removeEventListener('tour:start-current', startCurrentTour)

  const oid = absenceCheckFor.value ?? authStore.user?.id
  if (oid) {
    unsubscribeFromChannel(`operator:${oid}`)
  }
})

watch(
  () => notificationStore.callbackAlert,
  (alert) => {
    if (alert && callbackModalRef.value) {
      callbackModalRef.value.show(alert)
    }
  }
)
</script>

<template>
  <div class="flex h-screen overflow-hidden bg-background">
    <!-- Desktop sidebar wrapper
         - width fijo w-16 (iconos) siempre, para mantener layout
         - sidebar con w-60 se posiciona absolute en hover-expand, sin empujar contenido -->
    <div
      v-if="!isMobile"
      class="relative hidden w-16 shrink-0 md:flex"
      :class="{
        'w-60': !isCollapsed,
      }"
      @mouseenter="onSidebarMouseEnter"
      @mouseleave="onSidebarMouseLeave"
    >
      <div
        class="absolute inset-y-0 left-0 z-40 transition-[width] duration-300 ease-in-out"
        :class="{
          'w-60 shadow-2xl': sidebarExpanded,
          'w-16': !sidebarExpanded,
        }"
      >
        <AppSidebar
          :collapsed="!sidebarExpanded"
          @navigate="onNavigate"
          @expand="toggleSidebar"
        />
      </div>
    </div>

    <!-- Mobile sidebar (Sheet) -->
    <Sheet v-model:open="isMobileMenuOpen">
      <SheetContent side="left" class="w-72 p-0">
        <VisuallyHidden>
          <SheetTitle>Navegación principal</SheetTitle>
          <SheetDescription>Menú de navegación de la aplicación.</SheetDescription>
        </VisuallyHidden>
        <AppSidebar :collapsed="false" @navigate="onNavigate" />
      </SheetContent>
    </Sheet>

    <!-- Main column -->
    <div class="flex flex-1 flex-col overflow-hidden">
      <AppTopbar
        :collapsed="!sidebarExpanded"
        @toggle-sidebar="toggleSidebar"
        @toggle-mobile="toggleMobileMenu"
      />

      <main class="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6">
        <router-view v-slot="{ Component }">
          <transition name="fade" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </main>

      <footer
        class="flex h-10 shrink-0 items-center justify-center border-t border-border text-xs text-muted-foreground"
      >
        Data Lean Makers, S.L. © {{ currentYear }}
      </footer>
    </div>
  </div>

  <CallbackAlertModal ref="callbackModalRef" />

  <Dialog :open="absenceDialogOpen" @update:open="absenceDialogOpen = $event">
    <DialogContent class="sm:max-w-md" :show-close-button="false">
      <DialogHeader>
        <DialogTitle>¿Sigues trabajando?</DialogTitle>
        <DialogDescription>
          No hemos detectado actividad en los últimos minutos. Confirma si sigues en tu puesto.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter class="gap-2 sm:justify-end">
        <Button
          variant="outline"
          :disabled="absenceLoading"
          @click="markAbsent"
        >
          No, estoy ausente
        </Button>
        <Button
          :disabled="absenceLoading"
          @click="confirmStillWorking"
        >
          Sí, sigo trabajando
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <Tour
    v-if="currentTour"
    ref="tourRef"
    :tour-id="tourId"
    :steps="tourSteps"
    :auto-start="false"
    done-label="Finalizar"
  />

  <HelpFab @start-tour="startCurrentTour" @open-docs="() => router.push('/help')" />
</template>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>